import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Beaker, CheckCircle, Target, Award, UserSearch, Megaphone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Professionnels = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'strategie',
      icon: TrendingUp,
      title: t('professionnels.strategie'),
      description: t('professionnels.strategieDesc'),
      features: [
        t('professionnels.analyseMarche'),
        t('professionnels.objectifs'),
        t('professionnels.planAction'),
        t('professionnels.suiviKPI'),
        t('professionnels.optimisation')
      ]
    },
    {
      id: 'client-mystere',
      icon: UserSearch,
      title: t('professionnels.clientMystere'),
      description: t('professionnels.clientMystereDesc'),
      features: [
        t('professionnels.visitesMysteres'),
        t('professionnels.appelsMysteres'),
        t('professionnels.evaluationAccueil'),
        t('professionnels.rapportRecommandations'),
        t('professionnels.suiviAmelioration')
      ]
    },
    {
      id: 'formation',
      icon: Users,
      title: t('professionnels.formation'),
      description: t('professionnels.formationDesc'),
      features: [
        t('professionnels.techniquesVente'),
        t('professionnels.negociation'),
        t('professionnels.relationClient'),
        t('professionnels.leadership'),
        t('professionnels.coaching')
      ]
    },
    {
      id: 'hygiene',
      icon: Beaker,
      title: t('professionnels.hygiene'),
      description: t('professionnels.hygieneDesc'),
      features: [
        t('professionnels.auditInstallations'),
        t('professionnels.conformite'),
        t('professionnels.plansSanitaires'),
        t('professionnels.formationPersonnel'),
        t('professionnels.certification')
      ]
    },
    {
      id: 'marketing-digital',
      icon: Megaphone,
      title: t('professionnels.marketing'),
      description: t('professionnels.marketingDesc'),
      features: [
        t('professionnels.strategieDigital'),
        t('professionnels.creationSites'),
        t('professionnels.reseauxSociaux'),
        t('professionnels.creationContenu'),
        t('professionnels.campagnesPub')
      ]
    }
  ];

  return (
    <div data-testid="professionnels-page">
      {/* Hero Header */}
      <section className="page-header page-header-professionnels" data-testid="professionnels-header">
        <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans animate-fade-in">{t('home.space')} {t('home.professionnels')}</p>
        <div className="flex items-center justify-center gap-6 md:gap-8 mb-6 animate-fade-in-delay-1 flex-wrap">
          <Link 
            to="/professionnels/conseils" 
            className="group relative px-8 py-4 bg-[#C9A227] text-white font-serif text-2xl md:text-4xl hover:bg-[#b08d1f] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" 
            data-testid="pro-link-conseils"
          >
            {t('professionnels.conseils')}
            <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-white group-hover:w-3/4 transition-all duration-300"></span>
          </Link>
          <span className="text-[#C9A227] text-3xl md:text-5xl font-light">&</span>
          <Link 
            to="/professionnels/formations" 
            className="group relative px-8 py-4 bg-[#C9A227] text-white font-serif text-2xl md:text-4xl hover:bg-[#b08d1f] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" 
            data-testid="pro-link-formations"
          >
            {t('professionnels.formationsLink')}
            <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-white group-hover:w-3/4 transition-all duration-300"></span>
          </Link>
        </div>
        <div className="gold-line gold-line-center mb-6 animate-fade-in-delay-2"></div>
        <p className="text-white/80 text-lg max-w-2xl text-center animate-fade-in-delay-3">
          {t('professionnels.subtitle')}
        </p>
      </section>

      {/* Services Grid */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="services-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('professionnels.services')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
              {t('professionnels.solutions')}
            </h2>
            <div className="gold-line gold-line-center"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const isClientMystere = service.id === 'client-mystere';
              const CardWrapper = isClientMystere ? Link : 'div';
              const cardProps = isClientMystere ? { to: '/professionnels/client-mystere' } : {};
              
              return (
                <CardWrapper 
                  key={service.id} 
                  className={`service-card group ${isClientMystere ? 'cursor-pointer ring-1 ring-[#C9A227]/20 hover:ring-[#C9A227] block no-underline' : ''}`}
                  data-testid={`service-${service.id}`}
                  {...cardProps}
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-500">
                      <service.icon className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <h3 className="font-serif text-xl md:text-2xl text-[#1A1A1A]">{service.title}</h3>
                        {isClientMystere && (
                          <span className="text-xs uppercase tracking-wider bg-[#C9A227]/10 text-[#C9A227] px-2 py-1 border border-[#C9A227]/20" data-testid="cm-details-badge">
                            {t('professionnels.seeDetails')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-6">{service.description}</p>
                      <ul className="space-y-3">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Introduction - Notre Expertise */}
      <section className="section-luxury bg-white" data-testid="professionnels-intro">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('professionnels.expertise')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                {t('professionnels.accelerate')}
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('professionnels.expertiseText1')}
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {t('professionnels.expertiseText2')}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Target className="w-5 h-5 text-[#C9A227]" />
                  <span className="text-sm">+100 {t('professionnels.entreprises')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Award className="w-5 h-5 text-[#C9A227]" />
                  <span className="text-sm">98% {t('professionnels.satisfaction')}</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/260689/pexels-photo-260689.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Business Meeting" 
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#C9A227] text-white p-8 hidden md:block">
                <p className="font-serif text-4xl font-medium">10+</p>
                <p className="text-sm uppercase tracking-widest mt-2">{t('professionnels.secteurs')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-luxury bg-[#FDFCF8]" data-testid="benefits-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-white border border-[#F5EFD3]">
                  <p className="font-serif text-4xl text-[#C9A227] mb-2">+60%</p>
                  <p className="text-gray-600 text-sm">{t('professionnels.croissance')}</p>
                </div>
                <div className="p-6 bg-white border border-[#F5EFD3]">
                  <p className="font-serif text-4xl text-[#C9A227] mb-2">100%</p>
                  <p className="text-gray-600 text-sm">{t('professionnels.auditsReussis')}</p>
                </div>
                <div className="p-6 bg-white border border-[#F5EFD3]">
                  <p className="font-serif text-4xl text-[#C9A227] mb-2">+400</p>
                  <p className="text-gray-600 text-sm">{t('professionnels.collaborateurs')}</p>
                </div>
                <div className="p-6 bg-white border border-[#F5EFD3]">
                  <p className="font-serif text-4xl text-[#C9A227] mb-2">98%</p>
                  <p className="text-gray-600 text-sm">{t('professionnels.recommandation')}</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('professionnels.results')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                {t('professionnels.concreteResults')}
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('professionnels.resultsText')}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('professionnels.methodology')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('professionnels.expertsConsultants')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('professionnels.personalizedSupport')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="section-luxury bg-[#1A1A1A]" data-testid="sectors-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('professionnels.secteurs')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              {t('professionnels.multiSector')}
            </h2>
            <div className="gold-line gold-line-center"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              'Agroalimentaire',
              'Pharmaceutique',
              'Cosmétique',
              'Industrie',
              'Distribution',
              'Services',
              'Santé',
              'Hôtellerie',
              'Restauration',
              'Logistique'
            ].map((sector) => (
              <div 
                key={sector}
                className="p-4 border border-white/20 text-center hover:border-[#C9A227] transition-colors"
              >
                <span className="text-white/80 text-sm">{sector}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-luxury bg-[#FDFCF8]" data-testid="professionnels-cta-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 rounded-full border-2 border-[#C9A227] flex items-center justify-center mx-auto mb-8">
              <TrendingUp className="w-10 h-10 text-[#C9A227]" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
              {t('professionnels.readyToDevelop')}
            </h2>
            <div className="gold-line gold-line-center mb-8"></div>
            <p className="text-gray-600 text-lg mb-10">
              {t('professionnels.contactForAnalysis')}
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="professionnels-final-cta">
              {t('professionnels.requestQuote')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Professionnels;
